import React, { useState, useEffect } from 'react';
import './ScheduleManager.css';

const ScheduleManager = ({ deviceId, onScheduleChange }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    type: 'power',
    time: '08:00',
    value: true,
    days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
    enabled: true
  });

  const dayLabels = {
    'mon': '월',
    'tue': '화',
    'wed': '수',
    'thu': '목',
    'fri': '금',
    'sat': '토',
    'sun': '일'
  };

  // 스케줄 목록 로드
  useEffect(() => {
    if (deviceId) {
      fetchSchedules();
    }
  }, [deviceId]);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/devices/${deviceId}/schedules`);
      const data = await response.json();

      if (data.success) {
        setSchedules(data.schedules);
      } else {
        console.error('스케줄 조회 실패:', data.error);
      }
    } catch (error) {
      console.error('스케줄 조회 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  // 스케줄 추가
  const handleAddSchedule = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/devices/${deviceId}/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newSchedule),
      });

      const data = await response.json();

      if (data.success) {
        alert('스케줄이 추가되었습니다.');
        setShowAddForm(false);
        setNewSchedule({
          type: 'power',
          time: '08:00',
          value: true,
          days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
          enabled: true
        });
        fetchSchedules();
        if (onScheduleChange) {
          onScheduleChange();
        }
      } else {
        alert(data.error || '스케줄 추가에 실패했습니다.');
      }
    } catch (error) {
      alert('서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 스케줄 삭제
  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('정말 이 스케줄을 삭제하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/schedules/${scheduleId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        alert('스케줄이 삭제되었습니다.');
        fetchSchedules();
        if (onScheduleChange) {
          onScheduleChange();
        }
      } else {
        alert(data.error || '스케줄 삭제에 실패했습니다.');
      }
    } catch (error) {
      alert('서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 요일 토글
  const toggleDay = (day) => {
    setNewSchedule(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  // 스케줄 타입별 값 포맷팅
  const formatScheduleValue = (schedule) => {
    if (schedule.type === 'power') {
      return schedule.value ? '전원 ON' : '전원 OFF';
    } else if (schedule.type === 'brightness') {
      return `휘도 ${schedule.value}%`;
    }
    return String(schedule.value);
  };

  return (
    <div className="schedule-manager">
      <div className="schedule-header">
        <h3>⏰ 스케줄 관리</h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="add-schedule-btn"
          disabled={loading}
        >
          {showAddForm ? '취소' : '+ 스케줄 추가'}
        </button>
      </div>

      {/* 스케줄 추가 폼 */}
      {showAddForm && (
        <div className="schedule-form">
          <h4>새 스케줄 추가</h4>

          <div className="form-group">
            <label>스케줄 타입:</label>
            <select
              value={newSchedule.type}
              onChange={(e) => setNewSchedule(prev => ({ ...prev, type: e.target.value }))}
              className="form-select"
            >
              <option value="power">전원 제어</option>
              <option value="brightness">휘도 제어</option>
            </select>
          </div>

          <div className="form-group">
            <label>시간:</label>
            <input
              type="time"
              value={newSchedule.time}
              onChange={(e) => setNewSchedule(prev => ({ ...prev, time: e.target.value }))}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>설정값:</label>
            {newSchedule.type === 'power' ? (
              <select
                value={newSchedule.value}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, value: e.target.value === 'true' }))}
                className="form-select"
              >
                <option value="true">전원 ON</option>
                <option value="false">전원 OFF</option>
              </select>
            ) : (
              <input
                type="number"
                min="0"
                max="100"
                value={newSchedule.value}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, value: parseInt(e.target.value) }))}
                className="form-input"
                placeholder="0-100"
              />
            )}
          </div>

          <div className="form-group">
            <label>요일:</label>
            <div className="days-selector">
              {Object.entries(dayLabels).map(([day, label]) => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`day-btn ${newSchedule.days.includes(day) ? 'active' : ''}`}
                  type="button"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button
              onClick={handleAddSchedule}
              className="save-btn"
              disabled={loading || newSchedule.days.length === 0}
            >
              저장
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="cancel-btn"
              disabled={loading}
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* 스케줄 목록 */}
      <div className="schedule-list">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>스케줄을 불러오는 중...</p>
          </div>
        ) : schedules.length === 0 ? (
          <div className="no-schedules">
            <p>등록된 스케줄이 없습니다.</p>
          </div>
        ) : (
          schedules.map((schedule) => (
            <div key={schedule.id} className="schedule-item">
              <div className="schedule-info">
                <div className="schedule-main">
                  <span className="schedule-time">{schedule.time}</span>
                  <span className="schedule-action">{formatScheduleValue(schedule)}</span>
                </div>
                <div className="schedule-details">
                  <span className="schedule-days">
                    {schedule.days.map(day => dayLabels[day]).join(', ')}
                  </span>
                  <span className={`schedule-status ${schedule.enabled ? 'enabled' : 'disabled'}`}>
                    {schedule.enabled ? '활성' : '비활성'}
                  </span>
                </div>
              </div>
              <div className="schedule-actions">
                <button
                  onClick={() => handleDeleteSchedule(schedule.id)}
                  className="delete-btn"
                  disabled={loading}
                >
                  삭제
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ScheduleManager; 